/**
 * The PDF letter of one outreach step. The owner signs it on their Mac: "Open
 * letter" hands the seat's local original to Jarvis Helper via
 * `jarvis://open` (`apps/jarvis-mac/…/LocalFileOpener.swift` decides what may
 * open), the owner signs it in Preview and saves, and the seat attaches the
 * signed file to the wave. The server copies open inline in a new tab.
 */
import { letterFileUrl, type LetterFileKind, type OutreachMessage } from "../../api/b2g";
import { formatBytes, letterAwaitsSignature, letterPill } from "../../lib/b2g";
import { formatRelative } from "../../lib/formatRelative";
import { localOpenUrl } from "../../lib/markdownUrls";
import { Pill } from "../Pill";

export function LetterCard({ message }: { message: OutreachMessage }) {
  const letter = message.letter;
  const pill = letterPill(message);
  if (!letter || !pill) return null;
  const toSign = letterAwaitsSignature(message);
  const signed = letter.signed;
  const reading = letter.review_copy;
  const serverCopies: { kind: LetterFileKind; label: string }[] = [
    { kind: "letter", label: "letter" },
    ...(signed ? [{ kind: "signed" as const, label: "signed" }] : []),
    ...(reading ? [{ kind: "review_copy" as const, label: "reading copy" }] : []),
  ];

  return (
    <div className="mt-2 rounded border border-rule bg-surface-card px-3 py-2 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-ink-low">Letter</span>
        <span className="font-mono text-ink min-w-0 break-all">{letter.name}</span>
        <Pill value={pill.label} tone={pill.tone} />
      </div>
      <div className="text-ink-low mt-1 tnum">
        {letter.pages} {letter.pages === 1 ? "page" : "pages"} · {formatBytes(letter.size)}
        {letter.reference ? (
          <>
            {" · ref "}
            <span className="font-mono">{letter.reference}</span>
          </>
        ) : null}
        {letter.deadline_days ? ` · answer due within ${letter.deadline_days} days of sending` : ""}
      </div>
      {letter.legal_basis ? (
        <div className="mt-1">
          <span className="text-ink-low">Legal basis: </span>
          {letter.legal_basis}
        </div>
      ) : null}
      {signed ? (
        <div className="text-ink-low mt-1 tnum">
          Signed copy · {signed.pages} {signed.pages === 1 ? "page" : "pages"} · {formatBytes(signed.size)} · attached{" "}
          {formatRelative(signed.attached_at)}
        </div>
      ) : null}
      {message.revision?.letter_changed ? <div className="text-warn-fg mt-1">Letter changed in this version</div> : null}
      {toSign ? (
        <p className="text-warn-fg mt-1">
          Open the letter, sign it in Preview and save – the seat attaches the signed file and this page updates.
        </p>
      ) : null}
      <div className="flex items-center gap-2 flex-wrap mt-2">
        {letter.local_path ? (
          <a
            className={toSign ? "btn btn-second btn-sm" : "btn btn-ghost btn-sm"}
            href={localOpenUrl(letter.local_path)}
            title={letter.local_path}
          >
            Open letter
          </a>
        ) : (
          <span className="text-ink-low" title="The seat submitted no local path for this letter">
            no local copy
          </span>
        )}
        {signed ? (
          // The approval binds to the server's signed file – show that one, not the local copy.
          <a
            className="btn btn-second btn-sm"
            href={letterFileUrl(message.id, "signed")}
            target="_blank"
            rel="noreferrer"
            title="The signed file exactly as it goes out"
          >
            View signed
          </a>
        ) : null}
        {reading?.local_path ? (
          <a className="btn btn-ghost btn-sm" href={localOpenUrl(reading.local_path)} title={reading.local_path}>
            Open reading copy
          </a>
        ) : null}
        <span className="text-ink-low ml-auto">
          server copy:{" "}
          {serverCopies.map((c, i) => (
            <span key={c.kind}>
              {i > 0 ? " · " : null}
              <a className="link-quiet" href={letterFileUrl(message.id, c.kind)} target="_blank" rel="noreferrer">
                {c.label}
              </a>
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
